import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import { useMediaQuery, useTheme } from "@mui/material";
import GalleryImageViewer from "../Admin/Gallery/GalleryImageViewer";

function GallerySection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [images, setImages] = useState([]);
  const [loadingGalleries, setLoadingGalleries] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    loadGalleries();
  }, []);

  useEffect(() => {
    if (!selectedGalleryId) return;
    loadImages(selectedGalleryId);
  }, [selectedGalleryId]);

  const selectedGallery = useMemo(() => {
    return galleries.find((gallery) => gallery.id === selectedGalleryId) || null;
  }, [galleries, selectedGalleryId]);

  const loadGalleries = async () => {
    setLoadingGalleries(true);

    try {
      const res = await fetch("/gallery/public/list/");
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      setGalleries(list);

      if (list.length) {
        setSelectedGalleryId(list[0].id);
      }
    } catch (error) {
      console.error(error);
      setGalleries([]);
    } finally {
      setLoadingGalleries(false);
    }
  };

  const loadImages = async (galleryId) => {
    setLoadingImages(true);

    try {
      const res = await fetch(`/gallery/public/images/${galleryId}/`);
      const data = await res.json();
      setImages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const openViewer = (index) => {
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  if (loadingGalleries) {
    return (
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!galleries.length) {
    return null;
  }

  return (
    <>
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{
              textAlign: "center",
              mb: { xs: 1.2, md: 1.5 },
              fontWeight: 800,
              fontSize: { xs: "1.9rem", sm: "2.4rem", md: "3rem" },
              lineHeight: 1.1,
              color: "primary.main",
            }}
          >
            {selectedGallery?.name || "Galería"}
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              color: "text.secondary",
              fontWeight: 600,
              fontSize: { xs: ".95rem", md: "1rem" },
              mb: { xs: 2.6, md: 3.2 },
            }}
          >
            Explore each gallery from the tournament
          </Typography>

          <Box
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(140,15,44,0.12)",
              backgroundColor: "#fff",
              p: { xs: 1.2, sm: 1.5 },
              mb: { xs: 3, md: 4 },
              boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                mb: 1.2,
                px: 0.4,
              }}
            >
              <PhotoLibraryRoundedIcon
                sx={{ color: "primary.main", fontSize: 20 }}
              />
              <Typography
                sx={{
                  fontWeight: 800,
                  color: "text.primary",
                  fontSize: ".95rem",
                }}
              >
                Select a gallery
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.2,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(140,15,44,0.18)",
                  borderRadius: "999px",
                },
              }}
            >
              {galleries.map((gallery) => {
                const active = selectedGalleryId === gallery.id;

                return (
                  <Button
                    key={gallery.id}
                    onClick={() => setSelectedGalleryId(gallery.id)}
                    variant={active ? "contained" : "outlined"}
                    sx={{
                      minWidth: { xs: 140, sm: 170 },
                      height: 46,
                      borderRadius: "14px",
                      px: 2.2,
                      flexShrink: 0,
                      fontWeight: 800,
                      borderColor: "rgba(140,15,44,0.18)",
                      color: active ? "#fff" : "primary.main",
                      backgroundColor: active ? "primary.main" : "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {gallery.name}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {loadingImages ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !images.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                No images available in this gallery.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box
              sx={{
                columnCount: { xs: 2, sm: 2 },
                columnGap: "8px",
              }}
            >
              {images.map((img, index) => (
                <Box
                  key={img.id}
                  onClick={() => openViewer(index)}
                  sx={{
                    position: "relative",
                    mb: 1,
                    borderRadius: "14px",
                    overflow: "hidden",
                    cursor: "zoom-in",
                    breakInside: "avoid",
                    backgroundColor: "#f5f5f5",
                    boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
                  }}
                >
                  <Box
                    component="img"
                    src={img.image}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />

                  {index === 0 && (
                    <>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(180deg, rgba(0,0,0,0) 48%, rgba(0,0,0,0.42) 100%)",
                          pointerEvents: "none",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          left: 10,
                          bottom: 10,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.7,
                          px: 1.2,
                          py: 0.7,
                          borderRadius: "999px",
                          backgroundColor: "rgba(255,255,255,0.16)",
                          color: "#fff",
                          backdropFilter: "blur(10px)",
                          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                          pointerEvents: "none",
                        }}
                      >
                        <TouchAppRoundedIcon
                          sx={{ fontSize: 18, color: "#fff" }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            lineHeight: 1,
                            letterSpacing: "0.01em",
                            color: "#fff",
                          }}
                        >
                          Toca para ampliar
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box className="row g-3 justify-content-center">
              {images.map((img, index) => (
                <Box key={img.id} className="col-6 col-md-4 col-lg-3">
                  <Box
                    onClick={() => openViewer(index)}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "16px",
                      cursor: "zoom-in",
                      aspectRatio: "1 / 1",
                      backgroundColor: "#f5f5f5",
                      boxShadow: "0 12px 34px rgba(15,23,42,0.10)",
                      transition: "transform 0.25s ease, box-shadow 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 18px 42px rgba(15,23,42,0.16)",
                      },
                      "&:hover .gallery-page-image": {
                        transform: "scale(1.06)",
                      },
                      "&:hover .gallery-zoom-overlay": {
                        opacity: 1,
                      },
                      "&:hover .gallery-zoom-badge": {
                        transform: "translateY(0) scale(1)",
                        opacity: 1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={img.image}
                      alt=""
                      className="gallery-page-image"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 0.35s ease",
                      }}
                    />

                    <Box
                      className="gallery-zoom-overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.42) 100%)",
                        opacity: 0,
                        transition: "opacity 0.25s ease",
                      }}
                    />

                    <Box
                      className="gallery-zoom-badge"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transform: "translateY(10px) scale(0.96)",
                        transition: "all 0.25s ease",
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(255,255,255,0.18)",
                          backdropFilter: "blur(10px)",
                          boxShadow: "0 14px 30px rgba(0,0,0,0.20)",
                          border: "1px solid rgba(255,255,255,0.18)",
                        }}
                      >
                        <ZoomInRoundedIcon
                          sx={{ fontSize: 30, color: "#fff" }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Container>
      </Box>

      <GalleryImageViewer
        open={viewerOpen}
        images={images}
        currentIndex={currentIndex}
        onClose={closeViewer}
        onChangeIndex={setCurrentIndex}
        galleryName={selectedGallery?.name || "Galería"}
      />
    </>
  );
}

export default GallerySection;